/**
 * Journey projection for one Production Line (production-lines): the five
 * tab keys, the twelve journey steps the server can put the page in, and
 * the pure reading (`journeyOf`) that takes the step from the intake view
 * and the latest creative run. No React, no fetches, no timers.
 */

import type {
  MerchantCreativeRunView,
  MerchantProfileView,
  ProductionLineReadView,
} from '../../merchant/api.js';

export type LineTab = 'overview' | 'brief' | 'analysis' | 'videos' | 'activity';
export const TABS: readonly LineTab[] = [
  'overview',
  'brief',
  'analysis',
  'videos',
  'activity',
];

export type Journey =
  | 'loading'
  | 'checking'
  | 'evidence'
  | 'profile'
  | 'analysis'
  | 'recommendations'
  | 'preparing'
  | 'card'
  | 'generating'
  | 'complete'
  | 'needs'
  | 'failed';

/** The modal the page has open, if any. */
export type LineModal = 'brief' | 'edit-profile' | 'pause' | 'archive' | null;

/** A configured Production Line: the read view once it is no longer pending. */
export type ConfiguredLine = Exclude<ProductionLineReadView, { pending: true }>;

/**
 * Which step of the journey the page shows. Every step is read from the
 * intake behind the line and its latest creative run; nothing here advances
 * on a timer.
 */
export function journeyOf(
  view: MerchantProfileView | null,
  detailFailed: boolean,
  latestRun: MerchantCreativeRunView | null,
  anotherVersion: boolean,
): Journey {
  if (view === null) return detailFailed ? 'failed' : 'loading';
  if (['COLLECTING', 'PROCESSING'].includes(view.status)) return 'loading';
  if (
    view.profileIssue.state === 'mouja_checking' &&
    view.profile?.reviewState !== 'APPROVED'
  )
    return 'checking';
  if (view.profile === null) return 'evidence';
  if (view.profile.reviewState !== 'APPROVED') return 'profile';
  if (view.analysis.progress.overallStatus !== 'completed') return 'analysis';
  if (latestRun === null || anotherVersion) return 'recommendations';
  if (latestRun.video !== null || latestRun.merchantState === 'READY')
    return 'complete';
  if (latestRun.merchantState === 'PREPARING') return 'preparing';
  if (latestRun.merchantState === 'REVIEW_CARD') return 'card';
  if (
    latestRun.merchantState === 'NEEDS_ONE_ITEM' ||
    latestRun.merchantState === 'DIRECTION_NEEDS_REFRESH'
  )
    return 'needs';
  if (
    latestRun.merchantState === 'COULD_NOT_CREATE' ||
    latestRun.merchantState === 'CANCELLED'
  )
    return 'failed';
  return 'generating';
}
